import { ChangeEvent, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  ModalProps,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import {
  CreateWalletMerchantDTO,
  DepositFiatWalletDTO,
  useGetCoinsByTypeQuery,
  useGetCoinsQuery,
  WithdrawFiatWalletDTO,
} from '../../../../../api';

export type WalletAction = 'deposit' | 'withdraw';
export type ActionFields = DepositFiatWalletDTO['body'] | WithdrawFiatWalletDTO['body'];

export type ActionModalProps = {
  open: boolean;
  onClose: () => void;
  fieldsHandler: (
    e: SelectChangeEvent | ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  fields: CreateWalletMerchantDTO['body'] | null;
  onCreate: () => void;
  isLoading: boolean;
};

export const CreateModal: FC<ActionModalProps> = (props) => {
  const { open, onClose, fields, fieldsHandler, onCreate, isLoading } = props;
  const { t } = useTranslation();
  const { data: coinList, isLoading: isCoinsLoading } = useGetCoinsByTypeQuery({ type: 'crypto' });

  // elements
  const coinSelectOptions = coinList?.coins.map((coin) => {
    return (
      <MenuItem key={coin.id} value={coin.id}>
        {coin.name}
      </MenuItem>
    );
  });

  // validation for required fields
  const requiredFields = ['coin_id'] as (keyof CreateWalletMerchantDTO['body'])[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (fields[field] === undefined) {
        return true;
      }
    }
    return false;
  };

  // close handlers
  const closeHandlerWithPrevent: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') return;

    onClose();
  };
  const closeHandler = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => closeHandlerWithPrevent(_, reason)}
      PaperProps={{ sx: { minWidth: '380px' } }}
    >
      <DialogTitle>{t('profile-wallets.create-modal.title')}</DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={() => closeHandler()}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            select
            name="coin_id"
            value={fields?.coin_id ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('profile-wallets.create-modal.fields.coin')}
            fullWidth
          >
            {coinSelectOptions}
          </TextField>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onCreate()}
          disabled={isCoinsLoading || isLoading || checkSubmitDisabling()}
        >
          {t('fees.modals.create.create-btn')}
        </Button>
        <Button variant="outlined" onClick={() => onClose()}>
          {t('fees.modals.cancel-btn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
